import React from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { resolveMarketingUrl } from '@/constants/routes.mapping';
import { FooterCertification, footerData } from '@/data/footerData';

interface FooterBottomProps {
  certifications: FooterCertification[];
}

const BADGE_HEIGHT_IN_PIXELS = 40;

const CertificationBadge = ({
  certification,
}: {
  certification: FooterCertification;
}) => {
  if (!certification.imageUrl) return null;

  const { imageUrl, imageAlt, name, imageWidth, imageHeight } = certification;
  const height = imageHeight ?? BADGE_HEIGHT_IN_PIXELS;
  const width = imageWidth ?? BADGE_HEIGHT_IN_PIXELS * 2;

  return (
    <Image
      src={imageUrl}
      alt={imageAlt || name}
      width={width}
      height={height}
      unoptimized
      className="h-10 w-auto object-contain"
      data-testid={`footer-certification-${certification.id}`}
    />
  );
};

export const FooterBottom: React.FC<FooterBottomProps> = ({
  certifications,
}) => {
  const logoData = footerData.logo;
  const copyrightText = footerData.copyrightText;
  const legalLinks = footerData.legalLinks || [];

  return (
    <div>
      <div className="flex items-center justify-between gap-8">
        {/* Logo */}
        {logoData && (
          <div className="shrink-0">
            <Link
              href={resolveMarketingUrl(logoData.href) || logoData.href || '/'}
            >
              <Image
                src={
                  logoData.url ||
                  '/images/logo/LIPS_Healthcare_at_Batterseas.png'
                }
                alt={logoData.alt || 'LIPS Healthcare'}
                width={373}
                height={112}
                className="h-28 w-auto object-contain"
              />
            </Link>
          </div>
        )}

        {/* Certifications */}
        {certifications.length > 0 && (
          <div className="flex items-center gap-6">
            {/* <span className="text-gray-300 text-sm font-medium">
              Regulated by:
            </span> */}
            {certifications.map((cert) => (
              <div key={cert.id} className="flex items-center">
                {cert.href ? (
                  <Link
                    href={cert.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center transition-opacity hover:opacity-80"
                  >
                    <CertificationBadge certification={cert} />
                  </Link>
                ) : (
                  <CertificationBadge certification={cert} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Copyright and Legal Links */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-6 gap-y-2">
        <p className="text-sm text-gray-300">{copyrightText}</p>
        {legalLinks.length > 0 && (
          <>
            {legalLinks.map((link) => (
              <Link
                key={link.id}
                href={resolveMarketingUrl(link.href) || link.href}
                className="text-sm text-gray-300 transition-colors hover:text-white"
              >
                {link.title}
              </Link>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
